import React from "react";
import { RouterProvider } from "@tanstack/react-router";
import { I18nextProvider } from "react-i18next";
import "./index.css";
import { queryClient, router, TanStackRouterDevtools } from "./state/router";
import i18n from "./state/i18n";
import { ThemeProvider } from "./state/theme";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import {
  TooltipContent,
  TooltipProvider,
} from "@/components/custom/tooltip.tsx";
import { DialogProvider } from "@/components/ui/dialog";
import { SelectInputCacheProvider } from "@/state/select-input-cache-context";
import { needsAccount, primeAccount } from "@/state/prime-account";

declare module "@tanstack/react-router" {
  interface Route {
    requiredPermissions?: readonly string[];
  }
}

const AppContent = () => {
  // On a signed-in URL the profile must be cached before the router validates the first URL --
  // route defaults such as a table's saved page size read it synchronously. See primeAccount.
  const [primed, setPrimed] = React.useState(
    () => !needsAccount(window.location.pathname),
  );
  React.useEffect(() => {
    if (primed) {
      return;
    }
    let cancelled = false;
    primeAccount(queryClient).finally(() => {
      if (!cancelled) {
        setPrimed(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [primed]);
  if (!primed) {
    return null;
  }
  return (
    <>
      <RouterProvider router={router} context={{ queryClient }} />
      <React.Suspense>
        <TanStackRouterDevtools
          position="bottom-right"
          toggleButtonProps={{
            style: { right: "80px", bottom: "15px" },
          }}
          router={router}
        />
      </React.Suspense>
      <TooltipContent />
    </>
  );
};

export const App = () => {
  return (
    <>
      <QueryClientProvider client={queryClient}>
        <ReactQueryDevtools initialIsOpen={false} client={queryClient} />
        <ThemeProvider defaultTheme="light" storageKey="ui-theme">
          <I18nextProvider i18n={i18n}>
            <DialogProvider>
              <SelectInputCacheProvider>
                <TooltipProvider place="top-start">
                  <AppContent />
                </TooltipProvider>
              </SelectInputCacheProvider>
            </DialogProvider>
          </I18nextProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </>
  );
};
